import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { db } from '../firebaseConfig/firebase';
import { collection, query, onSnapshot } from "firebase/firestore";
import { Container, Badge, Button, Form, Row, Col, Spinner, Table, Card, Nav, InputGroup } from 'react-bootstrap';
import Header from './Header';

const Resultats = () => {
    const [alumnos, setAlumnos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activePriority, setActivePriority] = useState('P1');

    const [filtros, setFiltros] = useState({
        alumno: '',
        tribunal: '',
    });

    useEffect(() => {
        const q = query(collection(db, "tribu-esp"));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const docs = [];
            querySnapshot.forEach((doc) => {
                docs.push({ ...doc.data(), id: doc.id });
            });
            setAlumnos(docs);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const datosProcesados = useMemo(() => {
        // 1. Filtro inicial por texto (Buscador)
        let base = alumnos.filter((item) => {
            return (
                item.ALUMNO?.toLowerCase().includes(filtros.alumno.toLowerCase()) &&
                item.TRIBUNAL?.toLowerCase().includes(filtros.tribunal.toLowerCase())
            );
        });

        let resultado;

        // 2. Lógica de agrupación y promedios por Prioridad
        if (activePriority !== 'TODOS') {
            const numPrioridad = activePriority.at(-1);
            const campoPrioridad = `PRIORIDAD_${numPrioridad}`;

            const grupos = base
                .filter(a => a[campoPrioridad] && String(a[campoPrioridad]) === String(a.TRIBUNAL))
                .reduce((acc, current) => {
                    const nombre = current.ALUMNO;
                    if (!acc[nombre]) {
                        acc[nombre] = {
                            ...current,
                            count: 1,
                            sumaEntrevista: parseFloat(current.NOTA_ENTREVISTA) || 0,
                            sumaPortfolio: parseFloat(current.NOTA_PORTFOLIO) || 0,
                            notaExpFija: parseFloat(current.NOTA_EXP) || 0, // Se queda fija
                            tribunales: [current.TRIBUNAL],
                            esAgrupado: false
                        };
                    } else {
                        acc[nombre].count += 1;
                        acc[nombre].sumaEntrevista += parseFloat(current.NOTA_ENTREVISTA) || 0;
                        acc[nombre].sumaPortfolio += parseFloat(current.NOTA_PORTFOLIO) || 0;
                        acc[nombre].esAgrupado = true;
                        if (!acc[nombre].tribunales.includes(current.TRIBUNAL)) {
                            acc[nombre].tribunales.push(current.TRIBUNAL);
                        }
                    }
                    return acc;
                }, {});

            resultado = Object.values(grupos).map(grupo => {
                const promedioEnt = (grupo.sumaEntrevista / grupo.count).toFixed(2);
                const promedioPort = (grupo.sumaPortfolio / grupo.count).toFixed(2);
                const notaExp = grupo.notaExpFija.toFixed(2);

                const notaFinal = (
                    (parseFloat(promedioEnt) + parseFloat(promedioPort) + parseFloat(notaExp)) / 3
                ).toFixed(2);

                return {
                    ...grupo,
                    NOTA_ENTREVISTA: promedioEnt,
                    NOTA_PORTFOLIO: promedioPort,
                    NOTA_EXP: notaExp,
                    TRIBUNAL: grupo.esAgrupado ? grupo.tribunales.join(", ") : grupo.TRIBUNAL,
                    TOTAL_CANDIDATO: notaFinal
                };
            });
        } else {
            // Para la vista "TODOS", calculamos la nota final si no viene calculada de Firebase
            resultado = base.map(a => ({
                ...a,
                TOTAL_CANDIDATO: a.TOTAL_CANDIDATO || (((parseFloat(a.NOTA_ENTREVISTA) || 0) + (parseFloat(a.NOTA_PORTFOLIO) || 0) + (parseFloat(a.NOTA_EXP) || 0)) / 3).toFixed(2)
            }));
        }

        // 3. ORDENACIÓN FINAL (Descendente por Nota Final)
        return resultado.sort((a, b) => parseFloat(b.TOTAL_CANDIDATO) - parseFloat(a.TOTAL_CANDIDATO));

    }, [alumnos, activePriority, filtros]);

    const exportToExcel = () => {
        const reportData = datosProcesados.map(a => ({
            Alumno: a.ALUMNO,
            Tribunal: a.TRIBUNAL,
            Nota_Entrevista: a.NOTA_ENTREVISTA,
            Nota_Portfolio: a.NOTA_PORTFOLIO,
            Nota_Exp: a.NOTA_EXP,
            Total: a.TOTAL_CANDIDATO,
            Prioridad_Info: activePriority === 'TODOS' ? `P1: ${a.PRIORIDAD_1}` : `${activePriority}: ${a[`PRIORIDAD_${activePriority.at(-1)}`]}`,
            Evaluaciones: a.count || 1
        }));

        const ws = XLSX.utils.json_to_sheet(reportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Resultados");
        XLSX.writeFile(wb, `Reporte_${activePriority}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const handleFilterChange = (e) => {
        setFiltros({ ...filtros, [e.target.name]: e.target.value });
    };

    if (loading) return <Container className="text-center mt-5"><Spinner animation="border" variant="primary" /></Container>;

    return (
        <>
            <Header />
            <Container fluid className="mt-4 px-4 pb-5">
                <Row className="mb-3 align-items-center">
                    <Col md={8}>
                        <h2 className="fw-bold text-dark">Dashboard de Resultados</h2>
                        <Badge bg="secondary" className="me-2">Vista: {activePriority}</Badge>
                    </Col>
                    <Col md={4} className="text-end">
                        <Button variant="dark" onClick={exportToExcel} disabled={datosProcesados.length === 0}>
                            <i className="bi bi-file-earmark-excel"></i> Exportar
                        </Button>
                    </Col>
                </Row>

                <Card className="mb-4 shadow-sm border-0">
                    <Card.Header className="bg-white border-0 pt-3">
                        <Nav variant="underline" activeKey={activePriority} onSelect={(k) => setActivePriority(k)}>
                            {/*<Nav.Item><Nav.Link eventKey="TODOS" className="px-4 fw-bold text-dark">Sin filtros</Nav.Link></Nav.Item>*/}
                            <Nav.Item><Nav.Link eventKey="P1" className="px-4 text-uppercase fw-bold text-dark">Prioridad 1</Nav.Link></Nav.Item>
                            <Nav.Item><Nav.Link eventKey="P2" className="px-4 text-uppercase fw-bold text-dark">Prioridad 2</Nav.Link></Nav.Item>
                            <Nav.Item><Nav.Link eventKey="P3" className="px-4 text-uppercase fw-bold text-dark">Prioridad 3</Nav.Link></Nav.Item>
                        </Nav>
                    </Card.Header>
                    <Card.Body className="bg-light border-top">
                        <Row>
                            <Col md={6} className="mb-2">
                                <InputGroup>
                                    <InputGroup.Text>🔍</InputGroup.Text>
                                    <Form.Control
                                        name="alumno"
                                        placeholder="Buscar por nombre o email..."
                                        onChange={handleFilterChange}
                                    />

                                </InputGroup>
                            </Col>
                            <Col md={6} className="mb-2">
                                <InputGroup>
                                    <InputGroup.Text>⚖️</InputGroup.Text>
                                    <Form.Control
                                        name="tribunal"
                                        placeholder="Filtrar por tribunal..."
                                        onChange={handleFilterChange}
                                    />

                                </InputGroup>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                <div className="table-responsive shadow-sm rounded bg-white">
                    <Table hover className="mb-0">
                        <thead className="table-secondary">
                            <tr className="text-center">
                                <th className="text-start ps-4">Alumno</th>
                                <th>Tribunal</th>
                                <th>Entrevista</th>
                                <th>Portfolio</th>
                                <th>Exp.</th>
                                <th>Nota Final</th>
                                <th className="pe-4">Detalle Prioridad</th>
                            </tr>
                        </thead>
                        <tbody>
                            {datosProcesados.length > 0 ? datosProcesados.map((alumno) => (
                                <tr key={alumno.id} style={{ verticalAlign: 'middle' }} className="text-center">
                                    <td className="text-start ps-4">
                                        <div className="fw-bold text-dark">{alumno.ALUMNO}</div>
                                        {alumno.esAgrupado && (
                                            <Badge bg="dark" style={{ fontSize: '0.7rem' }}>
                                                {alumno.count} EVALUACIONES
                                            </Badge>
                                        )}
                                    </td>
                                    <td>
                                        <div className="text-truncate mx-auto" style={{ maxWidth: '250px' }}>
                                            <Badge bg="secondary" pill className="fw-normal">{alumno.TRIBUNAL}</Badge>
                                        </div>
                                    </td>
                                    <td className={alumno.esAgrupado ? "text-dark fw-bold" : ""}>{alumno.NOTA_ENTREVISTA}</td>
                                    <td className={alumno.esAgrupado ? "text-dark fw-bold" : ""}>{alumno.NOTA_PORTFOLIO}</td>
                                    <td className={alumno.esAgrupado ? "text-dark fw-bold" : ""}>{alumno.NOTA_EXP}</td>
                                    <td>
                                        <Badge
                                            bg={parseFloat(alumno.TOTAL_CANDIDATO) >= 5 ? "success" : "danger"}
                                            className="px-3 py-2"
                                        >
                                            {alumno.TOTAL_CANDIDATO}
                                        </Badge>
                                    </td>
                                    <td className="pe-4">
                                        {activePriority === 'TODOS' ? (
                                            <div className="small text-muted">
                                                P1: {alumno.PRIORIDAD_1 || '-'} | P2: {alumno.PRIORIDAD_2 || '-'} | P3: {alumno.PRIORIDAD_3 || '-'}
                                            </div>
                                        ) : (
                                            <Badge bg="light" text="dark" className="border">
                                                {alumno[`PRIORIDAD_${activePriority.at(-1)}`]}
                                            </Badge>
                                        )}
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="7" className="text-center py-5 text-muted">
                                        No se han encontrado registros para la prioridad <strong>{activePriority}</strong>.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </Container>
        </>
    );
};

export default Resultats;